import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { z } from 'zod'
import { CompanyDot, directoryQueryOptions, findCompany } from '@/entities/directory'
import { EntityTagsEditor, entityTagsQueryOptions } from '@/entities/tag'
import { fromISODate, toISODate } from '@/shared/lib'
import { Spinner } from '@/shared/ui/spinner'
import { CalendarBoard } from '@/widgets/calendar-board'

const searchSchema = z.object({
  view: z.enum(['day', 'week', 'month']).optional().catch(undefined),
  date: z.string().optional().catch(undefined),
})

export const Route = createFileRoute('/_app/companies/$companyId')({
  validateSearch: (search: Record<string, unknown>) => searchSchema.parse(search),
  component: CompanyCalendarPage,
})

function CompanyCalendarPage() {
  const { companyId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = Route.useNavigate()
  const view = search.view ?? 'month'
  const anchor = (search.date ? fromISODate(search.date) : null) ?? new Date()
  const { data: companies = [] } = useQuery(directoryQueryOptions())
  const company = findCompany(companies, companyId)

  return (
    <div className="flex h-full min-h-0 flex-col">
      {company && (
        <div className="flex flex-wrap items-center gap-2 border-b px-gutter py-2">
          <span className="label">Линза · компания</span>
          <span className="flex items-center gap-1.5 text-sm">
            <CompanyDot color={company.color} />
            {company.name}
            {company.isArchived && <span className="text-text-tertiary">· архив</span>}
          </span>
          {company.projects
            .filter((p) => !p.isArchived)
            .map((p) => (
              <Link
                key={p.id}
                to="/projects/$projectId"
                params={{ projectId: p.id }}
                className="rounded-pill border px-2 py-0.5 text-xs text-text-secondary transition-colors hover:border-border-strong hover:text-text-primary"
              >
                {p.name}
              </Link>
            ))}
          <div className="ml-auto">
            <CompanyTags companyId={companyId} />
          </div>
        </div>
      )}
      <CalendarBoard
        lens={{ kind: 'company', id: companyId }}
        view={view}
        anchor={anchor}
        onViewChange={(v) => void navigate({ search: (s) => ({ ...s, view: v }) })}
        onAnchorChange={(d) => void navigate({ search: (s) => ({ ...s, date: toISODate(d) }) })}
        createDefaults={{ companyId }}
      />
    </div>
  )
}

// Теги компании (00-architecture § 5) принадлежат хабу и общие для сервисов
// экосистемы — читаются отдельной ручкой /api/tags/company/{id}, не полем
// справочника. Страница линзы — точка правки: в справочнике теги read-only.
function CompanyTags({ companyId }: { companyId: string }) {
  const tags = useQuery(entityTagsQueryOptions('company', companyId))
  if (tags.isPending) return <Spinner label="Загрузка тегов" />
  return <EntityTagsEditor entityKind="company" entityId={companyId} tags={tags.data ?? []} />
}
